import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import authApi from "../api/authApi";
import { STORAGE_KEYS, clearAuthStorage } from "../api/client";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize Auth State from Local Storage
  const initializeAuth = useCallback(async () => {
    const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

    if (storedToken) {
      setAccessToken(storedToken);
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          // ignore parsing error
        }
      }

      // Validate session with backend
      try {
        const freshUser = await authApi.getMe();
        setUser(freshUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(freshUser));
      } catch (err) {
        // If initial check fails and refresh also failed, clear state
        const remainingToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (!remainingToken) {
          setUser(null);
          setAccessToken(null);
        }
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    initializeAuth();

    // Listen for session expiry from API interceptor
    const handleSessionExpired = () => {
      setUser(null);
      setAccessToken(null);
      clearAuthStorage();
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, [initializeAuth]);

  // Login handler
  const login = async (credentials) => {
    const data = await authApi.login(credentials);
    const { user: userData, tokens } = data;

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));

    setUser(userData);
    setAccessToken(tokens.accessToken);
    return data;
  };

  // LinkedIn OAuth 2.0 login handler
  const loginWithLinkedIn = useCallback(async ({ code, redirect_uri }) => {
    const data = await authApi.loginWithLinkedIn({ code, redirect_uri });
    const { user: userData, tokens } = data;

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));

    setUser(userData);
    setAccessToken(tokens.accessToken);
    return data;
  }, []);

  // Signup handler
  const signup = async (payload) => {
    return await authApi.signup(payload);
  };

  // Verify Email handler
  const verifyEmail = async (token) => {
    return await authApi.verifyEmail({ token });
  };

  // Forgot Password handler
  const forgotPassword = async (email) => {
    return await authApi.forgotPassword({ email });
  };

  // Reset Password handler
  const resetPassword = async (token, newPassword) => {
    return await authApi.resetPassword({ token, newPassword });
  };

  // Logout current session
  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore network errors during logout
    } finally {
      clearAuthStorage();
      setUser(null);
      setAccessToken(null);
    }
  };

  // Revoke all sessions across all devices
  const revokeAllSessions = async () => {
    try {
      await authApi.revokeAllSessions();
    } catch {
      // Ignore network errors
    } finally {
      clearAuthStorage();
      setUser(null);
      setAccessToken(null);
    }
  };

  // Refresh user data manually
  const refreshUserProfile = async () => {
    try {
      const freshUser = await authApi.getMe();
      setUser(freshUser);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(freshUser));
      return freshUser;
    } catch (err) {
      console.error("Failed to refresh user profile:", err);
      return null;
    }
  };

  const value = {
    user,
    accessToken,
    isAuthenticated: Boolean(user && accessToken),
    isLoading,
    login,
    loginWithLinkedIn,
    signup,
    verifyEmail,
    forgotPassword,
    resetPassword,
    logout,
    revokeAllSessions,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
